import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { gestoriaApi } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Upload, FileText, ChevronDown, ChevronUp, Download,
  Search, AlertTriangle, CheckCircle2, CreditCard,
} from 'lucide-react';

// Documentos contractuales que gestoría entrega y el empleado devuelve firmados.
// Dos documentos: el contrato con su anexo de horas en un solo archivo, y el
// modelo 145.
const DOC_DEFS = [
  { key: 'contrato', field: 'contrato',
    label: 'Contrato y anexo de horas complementarias', short: 'Contrato' },
  { key: '145', field: 'modelo145', label: 'Modelo 145 IRPF', short: '145' },
];

// Sociedades de la franquicia. Todas comparten gestoría.
const COMPANIES = [
  { value: '', label: 'Todas las sociedades' },
  { value: 'mislu', label: 'Mislu' },
  { value: 'hielo', label: 'Hielo' },
  { value: 'denali', label: 'Denali' },
  { value: 'naciones', label: 'Naciones' },
];

export default function GestoriaPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(false);

  // Vista activa: documentación por entregar vs. documentación ya firmada.
  const [view, setView] = useState('pendientes');
  const [company, setCompany] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await gestoriaApi.getSubmissions();
      setSubmissions(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('No tienes permiso para acceder.');
        navigate('/');
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, navigate, load]);

  const handleFile = (subId, key, file) => {
    setFiles(prev => ({ ...prev, [`${subId}_${key}`]: file }));
  };

  const handleUpload = async (subId) => {
    const formData = new FormData();
    let hasFiles = false;

    DOC_DEFS.forEach(d => {
      const f = files[`${subId}_${d.field}`];
      if (f) {
        formData.append(`file_${d.field}`, f);
        formData.append(`label_file_${d.field}`, d.label);
        hasFiles = true;
      }
    });

    if (!hasFiles) { toast.error('Selecciona al menos un archivo.'); return; }

    setUploading(true);
    try {
      await gestoriaApi.uploadDocs(subId, formData);
      toast.success('Documentos subidos correctamente.');
      setFiles(prev => {
        const next = { ...prev };
        DOC_DEFS.forEach(d => delete next[`${subId}_${d.field}`]);
        return next;
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al subir.');
    }
    setUploading(false);
  };

  // ── Filtrado y reparto entre las dos vistas ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return submissions.filter(s => {
      if (company && s.company !== company) return false;
      if (!q) return true;
      return `${s.first_name} ${s.last_name}`.toLowerCase().includes(q)
        || (s.dni || '').toLowerCase().includes(q);
    });
  }, [submissions, company, search]);

  // "Firmados" = el empleado ha devuelto al menos un documento firmado.
  const signedList = filtered.filter(s => (s.signed_uploaded || []).length > 0);
  const pendingList = filtered.filter(s => (s.signed_uploaded || []).length === 0);
  const visible = view === 'firmados' ? signedList : pendingList;

  // Solo lo que sube el empleado: los documentos que genera el sistema (los
  // exámenes y el diploma de manipulador) no son documentación contractual.
  const signedFilesOf = (sub) => (sub.files || []).filter(f => f.source === 'employee');

  const renderCard = (sub) => {
    const isExpanded = expandedId === sub.id;
    const uploaded = sub.gestoria_uploaded || [];
    const signed = sub.signed_uploaded || [];
    const missingSigned = DOC_DEFS
      .filter(d => uploaded.includes(d.key) && !signed.includes(d.key))
      .map(d => d.short);
    const companyLabel = COMPANIES.find(c => c.value === sub.company)?.label || sub.company || '—';

    return (
      <div key={sub.id} className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', cursor: 'pointer', gap: 12 }}
          onClick={() => setExpandedId(isExpanded ? null : sub.id)}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{sub.first_name} {sub.last_name}</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
              DNI: {sub.dni || '—'} · {sub.restaurant_name} · {companyLabel}
            </div>
            {view === 'firmados' && missingSigned.length > 0 && (
              <div style={{ fontSize: '0.8rem', color: 'var(--amber-600, #b45309)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <AlertTriangle size={13} /> Faltan por firmar: {missingSigned.join(', ')}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {sub.has_identity_doc && (
              <span className="badge badge--info" style={{ fontSize: '0.7rem' }} title="Ha adjuntado su DNI o NIE">
                <CreditCard size={11} style={{ verticalAlign: 'middle' }} /> Identidad
              </span>
            )}
            {sub.has_bank_cert && (
              <span className="badge badge--info" style={{ fontSize: '0.7rem' }} title="Ha adjuntado el certificado de titularidad bancaria">
                <CreditCard size={11} style={{ verticalAlign: 'middle' }} /> Titularidad
              </span>
            )}
            {DOC_DEFS.filter(d => uploaded.includes(d.key)).map(d => (
              <span key={d.key} className="badge badge--success" style={{ fontSize: '0.7rem' }}>{d.short} ✓</span>
            ))}
          </div>
          {isExpanded ? <ChevronUp size={18} color="var(--gray-400)" /> : <ChevronDown size={18} color="var(--gray-400)" />}
        </div>

        {isExpanded && (
          <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--gray-100)' }}>
            <div style={{ paddingTop: 16 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginBottom: 8 }}>
                <strong>SS:</strong> {sub.social_security_number || '—'} · <strong>IBAN:</strong> {sub.iban || '—'}
              </div>

              {/* Ambos son obligatorios para pasar del paso 1; faltan en las
                  altas anteriores a esa norma o que aún no lo han terminado. */}
              {(!sub.has_identity_doc || !sub.has_bank_cert) && (
                <div className="alert alert--info" style={{ fontSize: '0.82rem', marginBottom: 12 }}>
                  Falta {[!sub.has_identity_doc && 'la copia del DNI o NIE',
                          !sub.has_bank_cert && 'el certificado de titularidad bancaria']
                    .filter(Boolean).join(' y ')}.
                </div>
              )}

              {signedFilesOf(sub).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--gray-600)', marginBottom: 6 }}>
                    Documentación subida por el empleado:
                  </div>
                  {signedFilesOf(sub).map((f, i) => (
                    <a key={i} href={f.file} target="_blank" rel="noreferrer"
                      style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--blue-600)', marginBottom: 4 }}>
                      <Download size={12} /> {f.label}
                      <span style={{ marginLeft: 'auto', color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                        {new Date(f.uploaded_at).toLocaleDateString('es-ES')}
                      </span>
                    </a>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {DOC_DEFS.map(d => (
                  <div key={d.field}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                      <FileText size={14} style={{ verticalAlign: 'middle' }} /> {d.label}
                      {uploaded.includes(d.key) && <span style={{ color: 'var(--success)', marginLeft: 6 }}>✓ entregado</span>}
                      {signed.includes(d.key) && <span style={{ color: 'var(--success)', marginLeft: 6 }}>· firmado ✓</span>}
                    </label>
                    <input type="file" accept=".pdf" className="form-input" style={{ padding: 8 }}
                      onChange={e => handleFile(sub.id, d.field, e.target.files[0])} />
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button className="btn btn--primary" onClick={() => handleUpload(sub.id)} disabled={uploading}>
                  <Upload size={14} /> {uploading ? 'Subiendo...' : 'Subir documentos'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page-content" style={{ maxWidth: 900 }}>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h1>Panel de Gestoría</h1>
          <p style={{ color: 'var(--gray-500)' }}>
            Entrega el contrato, el anexo de horas y el Modelo 145, y descarga la documentación firmada
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className={`btn btn--sm ${view === 'pendientes' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => { setView('pendientes'); setExpandedId(null); }}>
          <Upload size={14} /> Pendientes de firma ({pendingList.length})
        </button>
        <button className={`btn btn--sm ${view === 'firmados' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => { setView('firmados'); setExpandedId(null); }}>
          <CheckCircle2 size={14} /> Documentación firmada ({signedList.length})
        </button>
      </div>

      <div className="filter-bar" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="form-input" value={company} onChange={e => setCompany(e.target.value)} style={{ maxWidth: 220 }}>
          {COMPANIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <Search size={15} color="var(--gray-400)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input className="form-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre o DNI" style={{ paddingLeft: 32, width: '100%' }} />
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        visible.length === 0 ? (
          <div className="empty-state">
            <p>
              {view === 'firmados'
                ? 'Todavía no hay empleados que hayan subido documentación firmada.'
                : 'No hay empleados pendientes con estos filtros.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visible.map(renderCard)}
          </div>
        )
      )}
    </div>
  );
}
